import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { ContactGroupEnum } from 'components/Connections/types';
import { getSetupData } from 'additiv-services/setup';
import { notification } from 'ui-library';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useClientProfile } from '../prodivers/clientProfile';
import { useCreatePortfolioActions, useCreatePortfolioSelectors } from './CreatePortfolio';
import { useGetCheckIfParentalAuthority } from '../hooks/rest/useGetCheckIfParentalAuthority';
import { OPTION_LIST, PRODUCTS } from '../constants/constants';
import { usePortfolioProcessLayoutActions } from './PortfolioProcessLayout/usePortfolioProcessLayoutActions';
import { useOptionList } from '../hooks/useOptionList';
import { usePortfolioProcessNavigation } from '../hooks/helpers/usePortfolioProcessNavigation';
import { useGetContactGroups } from '../hooks/rest/useGetContactGroups';
import { getRestrictedClientMessage } from '../widgets/ProductOffers/utils';

const setupData = getSetupData();

export const useProductOffers = () => {
    const { ...information }: any = useClientProfile();
    const { clientId } = useClientProfile();

    const {
        isPageLoadingSelector,
        pageErrorsSelector,
        currentClientSelector,
        selectedProductTypeSelector,
        is3bProductGroupSelector,
        selectedProductGroupIdSelector,
        isLoadingProducts,
        groupedProducts,
        errorProducts,
    } = useCreatePortfolioSelectors();

    const isMortgageProductSelector = useMemo(() => selectedProductTypeSelector === PRODUCTS.mortgage, [selectedProductTypeSelector]);

    const {
        setSelectedProductIdAction,
        setConnectionGroupIdAction,
        saveInvestorProfileClientAction,
    } = useCreatePortfolioActions();

    const {
        continueLoadingAction,
    } = usePortfolioProcessLayoutActions();

    const [showThreeBGroupsOrMortgageModal, setShowThreeBGroupsOrMortgageModal] = useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const [selectedMainInvestor, setSelectedMainInvestor] = useState<number | null>(clientId as number);

    const { data: contactGroups, refetch: contactGroupsRefetch } = useGetContactGroups({
        contactId: clientId,
        isEnabled: false,
        createThreeB: !isMortgageProductSelector,
        createMortgage: isMortgageProductSelector,
    });

    const { lists: [fatfGreylist, countries] } = useOptionList([OPTION_LIST.fatfGreylist, OPTION_LIST.country]);

    const { data: isFzAndFzpAlreadyAppliedData } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'checkIsFzAndFzpEnabled',
        payload: clientId,
        enabled: !!clientId,
    });

    const isFzAndFzpAlreadyApplied = useMemo(() => isFzAndFzpAlreadyAppliedData?.data, [isFzAndFzpAlreadyAppliedData?.data]);

    const clientFitFailMessage = useMemo(
        () => getRestrictedClientMessage({ data: currentClientSelector, selectedProductType: selectedProductTypeSelector, isMortgage: isMortgageProductSelector }),
        [currentClientSelector, selectedProductTypeSelector, isMortgageProductSelector],
    );

    const {
        data: isParentalAuthorityAvailable,
        isLoading: isParentalAuthorityLoading,
        error: getParentalAuthorityErrors,
    } = useGetCheckIfParentalAuthority({
        clientId,
        isEnabled: (is3bProductGroupSelector && currentClientSelector && currentClientSelector.age < 18 && !clientFitFailMessage),
    });

    const parentalAuthorityErrorMessage = useMemo(() => (
        (isParentalAuthorityAvailable === false && !isMortgageProductSelector) ? 'portfolio.createNewPortfolio.3bParentalAuthorityRequired' : undefined
    ), [isParentalAuthorityAvailable, isMortgageProductSelector]);

    const nationalityValidation = useMemo(() => {
        const countryOfNationality = countries?.find(f => f.id === currentClientSelector?.nationality);

        if (fatfGreylist?.some(s => s.label === countryOfNationality?.label && (is3bProductGroupSelector || isMortgageProductSelector))) {
            return 'portfolio.createNewPortfolio.nationalityOnFatfGrayList';
        }

        return null;
    }, [countries, fatfGreylist, is3bProductGroupSelector, currentClientSelector, isMortgageProductSelector]);

    const infoLabelData = useMemo(() => {
        if (!!clientFitFailMessage || !!parentalAuthorityErrorMessage || !!nationalityValidation) {
            return {
                isError: !!clientFitFailMessage,
                message: clientFitFailMessage || parentalAuthorityErrorMessage || nationalityValidation,
            };
        }

        return null;
    }, [clientFitFailMessage, parentalAuthorityErrorMessage, nationalityValidation]);

    const isLoading = useMemo(() => (information?.isLoading || isLoadingProducts || isPageLoadingSelector), [information?.isLoading || isLoadingProducts || isPageLoadingSelector]);
    const pageErrors = useMemo(() => (errorProducts || pageErrorsSelector || getParentalAuthorityErrors || undefined), [errorProducts || pageErrorsSelector || getParentalAuthorityErrors]);
    const continueInProgress = useMemo(() => (isParentalAuthorityLoading || saving), [isParentalAuthorityLoading || saving]);
    const continueDisabled = useMemo(
        () => (!selectedProductGroupIdSelector || selectedProductGroupIdSelector === -1 || !!clientFitFailMessage || continueInProgress || isLoading || !!pageErrors),
        [selectedProductGroupIdSelector, clientFitFailMessage, continueInProgress, pageErrors, isLoading],
    );

    const onThreeBConnectionGroupSelected = useCallback((groupId: string, isJa?: boolean) => {
        if (isMortgageProductSelector && groupId) {
            const additivCreditUrl = setupData.CREDIT_APP_URL;
            const ziagCreditUrl = setupData.CREDIT_APP_ZIAG_URL;
            const isZurichInvest = window.location.href.includes('zurich.ch');

            window.open(`${isZurichInvest ? ziagCreditUrl : additivCreditUrl}/${groupId}/mortgage-calculator`, '_blank', 'noopener,noreferrer');
            setShowThreeBGroupsOrMortgageModal(undefined);
        } else {
            if (isJa) {
                if (selectedMainInvestor) {
                    saveInvestorProfileClientAction(selectedMainInvestor);
                } else {
                    saveInvestorProfileClientAction(clientId as number);
                }
            }
            setConnectionGroupIdAction(groupId);

            stepComplete();
        }
    }, [selectedProductTypeSelector, selectedMainInvestor, clientId, setupData, isMortgageProductSelector]);

    const handle3bOrMortgageSelectedProduct = useCallback(async ({ type }) => {
        const { data } = await contactGroupsRefetch();

        if (type === PRODUCTS.mortgage) {
            const groupsForMortgage = data?.find(group => [ContactGroupEnum.mortgage]?.includes(group?.groupName));
            const groupsForJA = data?.find(group => [ContactGroupEnum.ja]?.includes(group?.groupName));

            if (groupsForMortgage) {
                if (groupsForJA) {
                    setShowThreeBGroupsOrMortgageModal(PRODUCTS.mortgage);
                } else {
                    onThreeBConnectionGroupSelected(groupsForMortgage?.groupId);
                }
            } else {
                notification.open({ content: 'Mortgage group not found!', type: 'error' });
            }
        } else {
            const filteredData = data?.filter(dta => dta?.groupName !== ContactGroupEnum.mortgage);

            if (filteredData?.length > 1) {
                setShowThreeBGroupsOrMortgageModal(type);
            } else {
                onThreeBConnectionGroupSelected(filteredData?.[0]?.groupId);
            }
        }
    }, [onThreeBConnectionGroupSelected]);

    const onContinue = useCallback(async () => {
        setSaving(true);
        if (isMortgageProductSelector) {
            await handle3bOrMortgageSelectedProduct({ type: PRODUCTS.mortgage });
        } else if (is3bProductGroupSelector) {
            await handle3bOrMortgageSelectedProduct({ type: PRODUCTS.bbb });
        } else {
            stepComplete();
        }
        setSaving(false);
    }, [is3bProductGroupSelector, selectedProductTypeSelector, handle3bOrMortgageSelectedProduct, isMortgageProductSelector]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        pageErrors,
        continueInProgress,
        continueDisabled,
    });

    useEffect(() => {
        continueLoadingAction(isParentalAuthorityLoading);
    }, [isParentalAuthorityLoading]);

    return {
        clientId,
        isLoading,
        pageErrors,
        groupedProducts,
        isFzAndFzpAlreadyApplied,
        infoLabelData,
        onContinue,
        setSelectedProductId: setSelectedProductIdAction,
        selectedProductId: selectedProductGroupIdSelector,
        onThreeBConnectionGroupSelected,
        contactGroups,
        showThreeBGroupsOrMortgageModal,
        setShowThreeBGroupsOrMortgageModal,
        onChangeMainClient: (_clientId) => setSelectedMainInvestor(parseInt(_clientId, 10)),
    };
};
